/**
 * Tela Hoje (fase 5): regras puras de recorte e ordenação dos blocos.
 * Os dados vêm das APIs de cada módulo (tarefas, publicações, agenda e
 * financeiro); faixa e alerta das tarefas e vencimentos continuam do servidor.
 */
import type { CalendarItem, EventType } from '@/types/calendar'
import { isLawyerLike } from '@/lib/roles'
import { byBand, type TaskBand, type TaskView } from '@/lib/tasks-board'

export const OFFICE_TIME_ZONE = 'America/Sao_Paulo'

/** Faixas que cabem na semana (a faixa "Demais" fica na tela de tarefas). */
export const WEEK_BANDS: readonly TaskBand[] = ['VENCIDAS', 'HOJE', 'PROXIMOS_3', 'PROXIMOS_7']

/** Data civil de hoje no fuso do escritório (AAAA-MM-DD). */
export function todayIso(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: OFFICE_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now)
}

/** "terça-feira, 21 de outubro" no fuso do escritório. */
export function todayLabel(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('pt-BR', { timeZone: OFFICE_TIME_ZONE, weekday: 'long', day: 'numeric', month: 'long' }).format(now)
}

/** AAAA-MM-DD + n dias corridos, sem passar pelo fuso local. */
export function addDays(isoDay: string, days: number): string {
  const [y, m, d] = isoDay.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10)
}

/** Tarefas da semana agrupadas por faixa (Vencidas → Próximos 7 dias). */
export function weekBands(tasks: TaskView[]): Array<{ key: TaskBand; tasks: TaskView[] }> {
  return byBand(tasks).filter((g) => WEEK_BANDS.includes(g.key))
}

export interface LawyerRef {
  id: string
  nome: string
}

/** Quem aparece no bloco de publicações: sócios e advogados ativos; a pessoa logada primeiro. */
export function lawyersForInbox(
  members: Array<{ id: string; fullName: string; email: string; role: string; isActive: boolean }>,
  currentUserId: string | undefined
): LawyerRef[] {
  return members
    .filter((m) => m.isActive && isLawyerLike(m.role))
    .map((m) => ({ id: m.id, nome: m.id === currentUserId ? `${m.fullName || m.email} (eu)` : m.fullName || m.email }))
    .sort((a, b) => (a.id === currentUserId ? -1 : b.id === currentUserId ? 1 : a.nome.localeCompare(b.nome, 'pt-BR')))
}

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  AUDIENCIA: 'Audiência',
  REUNIAO: 'Reunião',
  COMPROMISSO: 'Compromisso',
  OUTRO: 'Outro',
}

/** Compromissos do dia: sem cancelados; dia inteiro primeiro, depois por horário. */
export function eventsOfDay(items: CalendarItem[]): CalendarItem[] {
  return items
    .filter((i) => i.type === 'event' && i.eventStatus !== 'CANCELLED')
    .sort((a, b) => {
      const ka = a.allDay || !a.startTime ? '' : a.startTime
      const kb = b.allDay || !b.startTime ? '' : b.startTime
      return ka.localeCompare(kb)
    })
}

/** Link da listagem de lançamentos em aberto até a data (mesmo do aviso no sino). */
export function dueTransactionsHref(until: string): string {
  return `/finance-v2/transactions?status=OPEN&dueDateTo=${until}`
}
